/* Biglow inline header search (v2): icon opens the field to the left,
   black panel under the header with popular searches, wood swatches,
   and live product results + autofill suggestions while typing. */
(() => {
  if (customElements.get('inline-search')) return;

  class InlineSearch extends HTMLElement {
    connectedCallback() {
      // Lives in its own header-group section; move it into the header's icon row, just before the cart.
      const icons = document.querySelector('#site-header .area--icons');
      if (icons && !icons.contains(this)) {
        const cart = icons.querySelector('.cart-button');
        icons.insertBefore(this, cart || icons.firstChild);
        return; // connectedCallback runs again after the move
      }
      if (this.ready) return;
      this.ready = true;

      this.header = document.getElementById('site-header');
      this.toggle = this.querySelector('.biglow-search__toggle');
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('.biglow-search__panel');
      this.products = this.panel.querySelector('[data-products]');
      this.sugs = this.panel.querySelector('[data-sugs]');
      this.allLink = this.panel.querySelector('[data-all]');
      this.url = this.dataset.url || '/search/suggest';
      this.searchUrl = this.form.getAttribute('action') || '/search';
      this.cache = {};
      this.active = -1;
      this.timer = null;

      this.toggle.addEventListener('click', () => (this.isOpen() ? this.close(true) : this.open()));
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.lookup(), 150);
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.form.addEventListener('submit', (e) => {
        if (!this.input.value.trim()) e.preventDefault();
      });
      document.addEventListener('click', (e) => {
        if (this.isOpen() && !this.contains(e.target)) this.close(false);
      });
      window.addEventListener('resize', () => this.isOpen() && this.place());
      window.addEventListener('scroll', () => this.isOpen() && this.place(), { passive: true });
    }

    isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.input.tabIndex = 0;
      this.setMode(false);
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.place();
      setTimeout(() => this.input.focus(), 60);
    }

    close(returnFocus) {
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.input.value = '';
      this.input.tabIndex = -1;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.products.innerHTML = '';
      this.sugs.innerHTML = '';
      this.active = -1;
      if (returnFocus) this.toggle.focus();
    }

    // Pin the panel just under the header, right-aligned with it (full width on phones).
    place() {
      const r = (this.header || this).getBoundingClientRect();
      this.panel.style.top = `${Math.max(0, r.bottom)}px`;
      if (window.innerWidth < 768) {
        this.panel.style.left = '0px';
        this.panel.style.right = '0px';
      } else {
        this.panel.style.left = 'auto';
        this.panel.style.right = `${Math.max(16, window.innerWidth - r.right + 16)}px`;
      }
    }

    // typing = true shows products + suggestions, false shows popular searches
    setMode(typing) {
      this.panel.querySelectorAll('[data-typing-only]').forEach((el) => (el.hidden = !typing));
      this.panel.querySelectorAll('[data-empty-only]').forEach((el) => (el.hidden = typing));
      this.active = -1;
      this.clearActive();
    }

    lookup() {
      const q = this.input.value.trim();
      if (!q) return this.setMode(false);
      if (this.cache[q]) return this.render(this.cache[q], q);

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][fields]': 'title,product_type,variants.title,tag',
        section_id: 'biglow-search-suggestions',
      });

      fetch(`${this.url}?${params.toString()}`)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const p = doc.querySelector('[data-biglow-products]');
          const s = doc.querySelector('[data-biglow-results]');
          const result = { products: p ? p.innerHTML.trim() : '', sugs: s ? s.innerHTML.trim() : '' };
          this.cache[q] = result;
          if (this.input.value.trim() === q) this.render(result, q);
        })
        .catch(() => {});
    }

    render(result, q) {
      this.products.innerHTML =
        result.products || '<p class="biglow-search__empty">No pieces match yet. Try another word, or a wood below.</p>';
      this.sugs.innerHTML = result.sugs;
      this.highlight(q);
      this.allLink.href = `${this.searchUrl}?q=${encodeURIComponent(q)}`;
      this.allLink.firstChild.textContent = `See all results for “${q}” `;
      this.setMode(true);
      if (!result.sugs) this.sugs.closest('[data-typing-only]').hidden = true;
      this.place();
    }

    highlight(q) {
      const needle = q.toLowerCase();
      this.panel.querySelectorAll('.biglow-search__opt, .biglow-search__product-title').forEach((el) => {
        const text = el.textContent.trim();
        const i = text.toLowerCase().indexOf(needle);
        if (i < 0) return;
        const b = document.createElement('b');
        b.textContent = text.slice(i, i + q.length);
        el.textContent = '';
        el.append(text.slice(0, i), b, text.slice(i + q.length));
      });
    }

    navItems() {
      return [...this.panel.querySelectorAll('[data-nav]')].filter((el) => el.offsetParent !== null);
    }

    clearActive() {
      this.panel.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    }

    onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close(true);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      const items = this.navItems();
      if (e.key === 'Enter') {
        if (this.active >= 0 && items[this.active]) {
          e.preventDefault();
          window.location.href = items[this.active].href;
        }
        return;
      }
      if (!items.length) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      this.active = Math.max(-1, Math.min(items.length - 1, this.active + step));
      this.clearActive();
      if (this.active >= 0) {
        items[this.active].classList.add('is-active');
        items[this.active].scrollIntoView({ block: 'nearest' });
      }
    }
  }

  customElements.define('inline-search', InlineSearch);
})();
